import React from "react";
import { ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import CustomerTag from "@/components/CustomerTag";
import StatusBadge from "@/components/StatusBadge";
import { cn } from "@/lib/utils";

// Selection cell for tables with row selection
export const SelectionCell = {
  id: "select",
  header: ({ table }: any) => (
    <Checkbox
      checked={table.getIsAllPageRowsSelected()}
      onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
      aria-label="Select all"
    />
  ),
  cell: ({ row }: any) => (
    <Checkbox
      checked={row.getIsSelected()}
      onCheckedChange={(value) => row.toggleSelected(!!value)}
      aria-label="Select row"
    />
  ),
  enableSorting: false,
  enableHiding: false,
  size: 50,
};

// Sortable header component
export const SortableHeader = ({ 
  column, 
  children, 
  className 
}: { 
  column: any; 
  children: React.ReactNode; 
  className?: string; 
}) => (
  <Button
    variant="ghost"
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    className={cn("h-8 p-0 font-semibold", className)}
  >
    {children}
    <ArrowUpDown className="ml-2 h-4 w-4" />
  </Button>
);

// User cell with avatar and info
export const UserCell = ({ 
  user, 
  showEmail = true,
  onClick 
}: { 
  user: { 
    firstName: string; 
    lastName: string; 
    displayName?: string;
    email?: string; 
  };
  showEmail?: boolean;
  onClick?: () => void;
}) => {
  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <div 
      className={cn(
        "flex items-center gap-3",
        onClick && "cursor-pointer hover:text-primary transition-colors"
      )}
      onClick={onClick}
    >
      <Avatar>
        <AvatarFallback>
          {getInitials(user.firstName, user.lastName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">
          {user.displayName || `${user.firstName} ${user.lastName}`}
        </div>
        {showEmail && user.email && (
          <div className="text-sm text-muted-foreground truncate">
            {user.email}
          </div>
        )}
      </div>
    </div>
  );
};

// Customer cell with consistent styling
export const CustomerCell = ({ customer }: { customer: string }) => (
  <CustomerTag customer={customer || 'Not Available'} />
);

// Status cell with badge
export const StatusCell = ({ 
  status, 
  variant = "default" 
}: { 
  status: string | boolean; 
  variant?: "default" | "boolean";
}) => {
  if (variant === "boolean" && typeof status === "boolean") {
    return (
      <Badge 
        variant={status ? "default" : "secondary"} 
        className={status ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : ""}
      >
        {status ? "Active" : "Inactive"}
      </Badge>
    );
  }
  
  return <StatusBadge status={status as string} />;
};

// Department cell with icon
export const DepartmentCell = ({ department }: { department: string }) => (
  <div className="flex items-center gap-2">
    <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center">
      <span className="text-xs text-muted-foreground">
        {department?.charAt(0) || 'U'}
      </span>
    </div>
    <span className="truncate">{department || 'Unassigned'}</span>
  </div>
);

// Groups cell with badges
export const GroupsCell = ({ groups }: { groups: string[] }) => (
  <div className="flex flex-wrap gap-1">
    {groups.slice(0, 2).map((group) => (
      <Badge key={group} variant="outline" className="text-xs">
        {group}
      </Badge>
    ))}
    {groups.length > 2 && (
      <Badge variant="outline" className="text-xs">
        +{groups.length - 2} more
      </Badge>
    )}
  </div>
);

// Date/time cell with consistent formatting
export const DateTimeCell = ({ 
  date, 
  format = "datetime" 
}: { 
  date: string | Date; 
  format?: "date" | "time" | "datetime" | "relative";
}) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  const formatDate = () => {
    switch (format) {
      case "date":
        return dateObj.toLocaleDateString();
      case "time":
        return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case "datetime":
        return dateObj.toLocaleDateString() + ' at ' + dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      case "relative":
        // This would need a utility function for relative time
        return dateObj.toLocaleDateString();
      default:
        return dateObj.toLocaleDateString();
    }
  };

  return (
    <span className="text-sm text-muted-foreground">
      {formatDate()}
    </span>
  );
};